import { z } from 'zod';
import { IssueStatus, RecurrencePattern } from '@momentum/types';

const recurrenceConfigSchema = z.object({
  pattern: z.nativeEnum(RecurrencePattern),
  dayOfMonth: z.number().min(1).max(31).optional(),
  intervalDays: z.number().min(1).optional(),
});

export const recurrenceEndConfigSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('after_occurrences'),
    count: z.number().min(1, 'Count must be at least 1'),
  }),
  z.object({
    type: z.literal('until_date'),
    date: z.string().min(1, 'Date is required'),
  }),
]);

const taskRecurrenceSchema = z
  .object({
    config: recurrenceConfigSchema,
    startDate: z
      .string()
      .min(1, 'Recurrence start date is required')
      .refine(
        (date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return new Date(date) >= today;
        },
        { message: 'Start date cannot be in the past' }
      ),
    endConfig: recurrenceEndConfigSchema.optional(),
  })
  .refine(
    (data) => {
      if (data.endConfig?.type === 'until_date') {
        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endConfig.date);
        return endDate > startDate;
      }
      return true;
    },
    {
      message: 'End date must be after start date',
      path: ['endConfig', 'date'],
    }
  );

export const createTaskSchema = z
  .object({
    title: z
      .string()
      .min(1, 'Title is required')
      .max(200, 'Title must be less than 200 characters'),
    description: z
      .string()
      .max(2000, 'Description must be less than 2000 characters')
      .optional()
      .or(z.literal('')),
    assignees: z.array(z.string()).min(1, 'At least one assignee is required'),
    link: z.string().optional(),
    collaborators: z.array(z.string()).optional(),
    startDate: z.string().optional(),
    dueDate: z.string().optional(),
    status: z.enum(IssueStatus),
    parent: z.string(),
    recurrence: taskRecurrenceSchema.optional(),
    viewers: z
      .object({
        users: z.array(z.string()),
        teams: z.array(z.string()),
      })
      .optional(),
  })
  .refine(
    (data) => {
      // Hard invariant: if recurrence exists, startDate must be undefined/null
      if (data.recurrence) {
        return data.startDate === undefined || data.startDate === null;
      }
      return true;
    },
    {
      message:
        'Hard invariant violation: task.startDate must be undefined/null when recurrence is enabled. Use recurrence.startDate instead.',
      path: ['startDate'],
    }
  )
  .refine(
    (data) => {
      // Status is optional when recurrence exists (defaults to TODO)
      if (data.recurrence && !data.status) {
        return true; // Valid - will default to TODO
      }
      if (!data.recurrence && !data.status) {
        return false; // Invalid - status required for non-recurring tasks
      }
      return true;
    },
    {
      message: 'Status is required for non-recurring tasks',
      path: ['status'],
    }
  );

export type CreateTaskFormData = z.infer<typeof createTaskSchema>;

export type SignalSuggestionItem = {
  id: string;
  signal: string;
  contextualTitle?: string;
  approved: boolean;
};

export const defaultValues: CreateTaskFormData = {
  title: '',
  description: '',
  assignees: [],
  status: IssueStatus.TODO,
  parent: '',
};
